// Quoting (Lane 1335, RFQ plan WS7): the RFQs XMEK forwards to Wingman, read
// and matched, with the facts a quoter needs, laid out as the mockup
// (docs/audits/2026-09-25_lane1309_quoting-mockup-template.html): a
// collapsible RFQ list on top, the selected RFQ below at full width. Shown
// only when /me says quoting_enabled; every API route also answers 404 when
// off. Wingman shows facts only; the quoter prices the quote (plan §0.3).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageShell } from '../../components/ui/DataPrimitives';
import QuotingRfq from './QuotingRfq';
import QuotingChat from './QuotingChat';
import QuotingFilePanel from './QuotingFilePanel';
import { useQuotingApi } from './quotingApi';
import { customerLabel, jobText, shortDate } from './quotingText';
import '../ListLinear.css';
import './Quoting.css';

const POLL_MS = 5000;

// Poll only while something is really moving: a job being read or updated,
// or a queued job / a re-rank the worker will pick up. Never while reading is
// paused or the daily limit is reached (it would never end; Codex 1335).
function needsPolling(rfq) {
  if (!rfq) return false;
  const state = rfq.job?.state;
  if (state === 'reading' || state === 'updating') return true;
  if (!rfq.intake_enabled || state === 'waiting') return false;
  // A re-rank shows as a queued or running rematch job; a finished job never
  // polls, whatever its lines say (Codex 1335).
  return state === 'queued';
}

function RfqList({ list, current, onOpen, onMore, loadingMore }) {
  const [open, setOpen] = useState(true);
  const items = list.items;
  const shown = items.find((r) => r.rfq_id === current);
  return (
    <div className="qtng-box">
      <h3 className="qtng-boxh qtng-togl" onClick={() => setOpen((v) => !v)}>
        <span>RFQs <em>&nbsp;{items.length}{list.next_cursor ? '+' : ''} received{!open && shown ? ` · showing ${customerLabel(shown) || 'RFQ'}` : ''}</em></span>
        <em className="qtng-link">{open ? 'Hide list' : 'Show list'}</em>
      </h3>
      {open && (items.length ? (
        <div className="qtng-scroll">
          <table className="qtng-t">
            <thead><tr><th>Customer</th><th>RFQ #</th><th>Received</th><th>Reply due</th><th>Status</th><th className="qtng-r">Lines</th><th className="qtng-r">Parts found</th><th className="qtng-r">Has a part</th><th className="qtng-r">To confirm</th><th className="qtng-r">To pick</th></tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.rfq_id} className={`qtng-click ${r.rfq_id === current ? 'qtng-on' : ''}`} onClick={() => onOpen(r.rfq_id)}>
                  <td className="qtng-cut" style={{ maxWidth: 220 }}>{customerLabel(r) || (r.published ? 'Customer not named' : r.subject || 'New email')}</td>
                  <td>{r.customer_rfq_number || '–'}</td>
                  <td className="qtng-num">{shortDate(r.received_at)}</td>
                  <td className="qtng-cut" style={{ maxWidth: 200 }} title={r.reply_due_as_written || ''}>{r.reply_due_as_written || shortDate(r.reply_due_date)}</td>
                  <td className={r.job?.state === 'failed' ? 'qtng-bad' : r.job?.state === 'ready' ? 'qtng-mut' : 'qtng-mid'}>
                    {r.job?.state === 'ready' ? (r.document_kind === 'not_rfq' ? 'Not an RFQ' : 'Read') : jobText(r.job, list.intake_enabled)}</td>
                  <td className="qtng-r qtng-num">{r.published ? r.lines : '–'}</td>
                  {/* One number per cell (Ryan): found, filled in, waiting on the quoter. */}
                  <td className="qtng-r qtng-num">{r.published ? `${r.parts_found ?? r.with_part} of ${r.lines}` : '–'}</td>
                  <td className="qtng-r qtng-num">{r.published ? r.with_part : '–'}</td>
                  <td className={`qtng-r qtng-num ${r.to_confirm > 0 ? 'qtng-fill' : ''}`}>{r.published ? r.to_confirm : '–'}</td>
                  <td className="qtng-r qtng-num">{r.published ? (r.to_pick ?? 0) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.next_cursor && (
            <div className="qtng-more"><button type="button" className="qtng-btn qtng-btn--small" disabled={loadingMore} onClick={onMore}>{loadingMore ? 'Loading…' : 'Show older RFQs'}</button></div>
          )}
        </div>
      ) : <div className="qtng-empty">No RFQs yet. Forward an RFQ email to your Wingman quoting address and it appears here.</div>)}
    </div>
  );
}

export default function Quoting() {
  const api = useQuotingApi();
  const navigate = useNavigate();
  const { rfqId } = useParams();
  const [list, setList] = useState(null);
  const [rfq, setRfq] = useState(null);
  const [error, setError] = useState('');
  const [rfqError, setRfqError] = useState('');
  const [stale, setStale] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const timer = useRef(null);

  const loadList = useCallback(() => api.list()
    .then((r) => { setList(r); setError(''); return r; })
    .catch((e) => { setError(e.message); return null; }), [api]);

  const current = rfqId ? Number(rfqId) : (list?.items?.[0]?.rfq_id ?? null);

  // Only the answer to the latest request is shown, and never one with an
  // older account revision than the RFQ on screen: a slow poll from before
  // an account change cannot bring the old accounts back (Codex 1357).
  const asked = useRef(0);
  const loadRfq = useCallback(async () => {
    if (!current) { setRfq(null); return; }
    const mine = ++asked.current;
    try {
      const r = await api.rfq(current);
      if (mine !== asked.current) return;
      setRfq((old) => (old && old.rfq_id === r.rfq_id && (old.account_revision || 0) > (r.account_revision || 0)
        ? old : { ...r, _loadedAt: Date.now() }));
      setRfqError(''); setStale(false);
    } catch (e) {
      if (mine !== asked.current) return;
      // Keep what was shown, but say plainly it may be out of date (Codex 1335).
      setRfqError(e.message); setStale(true);
    }
  }, [api, current]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadRfq(); }, [loadRfq]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (needsPolling(rfq)) {
      timer.current = setTimeout(() => { loadRfq(); loadList(); }, POLL_MS);
    }
    return () => clearTimeout(timer.current);
  }, [rfq, loadRfq, loadList]);

  const reload = useCallback(async () => { await loadRfq(); await loadList(); }, [loadRfq, loadList]);

  const more = async () => {
    setLoadingMore(true);
    try {
      const next = await api.list(list.next_cursor);
      setList((l) => ({ ...next, items: [...l.items, ...next.items] }));
    } catch (e) { setError(e.message); } finally { setLoadingMore(false); }
  };

  const download = async (attachment) => {
    try {
      const blob = await api.download(current, attachment.rfq_attachment_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_filename || `file-${attachment.rfq_attachment_id}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setRfqError('Could not download the file. Try again.'); }
  };
  // The file open in the side panel (Lane 1399): {attachment, page, highlight}.
  const [file, setFile] = useState(null);
  useEffect(() => { setFile(null); }, [current]);
  const closeFile = useCallback(() => setFile(null), []);

  return (
    <PageShell className="ll qtng-ws">
      <div className="qtng-ws-main">
      <div className="ll-wrap qtng-page">
        <header className="ll-head">
          <div>
            <h1 className="ll-h1">Quoting</h1>
            <div className="ll-sub">RFQs forwarded to Wingman, read and matched to XMEK parts.</div>
          </div>
        </header>
        <div style={{ marginTop: 18 }}>
          {error && <div className="qtng-err">{error}</div>}
          {!list && !error && <div className="ll-loading">Loading…</div>}
          {list && <RfqList list={list} current={current} onOpen={(id) => navigate(`/quoting/${id}`)} onMore={more} loadingMore={loadingMore} />}
          {rfqError && (
            <div className="qtng-confirm" style={{ marginTop: 14 }}>
              {stale && rfq ? 'Could not refresh this RFQ. The figures below may be out of date. ' : `${rfqError} `}
              <button type="button" className="qtng-btn qtng-btn--small" onClick={reload}>Try again</button>
            </div>
          )}
          {rfq && rfq.rfq_id === current && !needsPolling(rfq)
            && ['queued', 'waiting'].includes(rfq.job?.state) && (
            <div className="qtng-acctrow" style={{ marginTop: 10 }}>
              <button type="button" className="qtng-btn qtng-btn--small" onClick={reload}>Refresh</button>
            </div>
          )}
          {rfq && rfq.rfq_id === current && (
            <div style={{ marginTop: 14, opacity: stale ? 0.6 : 1 }}>
              <QuotingRfq key={rfq.rfq_id} api={api} rfq={rfq} reload={reload} openFile={setFile} />
            </div>
          )}
        </div>
      </div>
      </div>
      {/* The Wingman chat about the open RFQ (Lane 1376 P1), docked on the right. */}
      <QuotingChat api={api} rfqId={current} />
      {file && rfq && rfq.rfq_id === current && (
        <QuotingFilePanel api={api} rfqId={current} file={file} onClose={closeFile} onDownload={download} />
      )}
    </PageShell>
  );
}
